// API-driven additional proof-document schema.

export const AUSTRALIAN_STATE_OPTIONS = [
  { value: 1, label: 'Australian Capital Territory' },
  { value: 2, label: 'New South Wales' },
  { value: 3, label: 'Northern Territory' },
  { value: 4, label: 'Queensland' },
  { value: 5, label: 'South Australia' },
  { value: 6, label: 'Tasmania' },
  { value: 7, label: 'Victoria' },
  { value: 8, label: 'Western Australia' },
];

export const ADDITIONAL_DOCUMENT_TYPES = [];

const normalise = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\/&]/g, ' ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const titleCase = (value) =>
  String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const FIELD_LABELS = {
  document_number: 'Document number',
  card_number: 'Card number',
  expiry_date: 'Expiry date',
  country_of_issue_id: 'Country of issue',
  state_of_issue: 'State of issue',
  registration_number: 'Registration number',
  registration_date: 'Registration date',
  registration_state: 'Registration state',
  acquisition_date: 'Acquisition date',
  date_of_event: 'Date of event',
  card_expiry: 'Card expiry',
  individual_ref_number: 'Individual reference number',
  card_type: 'Card type',
  certificate_issue_date: 'Certificate issue date',
  registration_year: 'Registration year',
  spouse_family_name: 'Spouse family name',
  spouse_given_name: 'Spouse given name',
  country_of_birth_id: 'Country of birth',
};

const getFieldLabel = (name) => FIELD_LABELS[name] || titleCase(name);

const choiceValue = (choice) =>
  choice?.value ?? choice?.code ?? choice?.id ?? choice;

const choiceLabel = (choice) =>
  choice?.label ??
  choice?.name ??
  choice?.value ??
  choice?.code ??
  String(choice);

const choicesFor = (documentType, fieldName) => {
  const choices = documentType?.field_choices;
  if (Array.isArray(choices)) return choices;
  if (choices && Array.isArray(choices[fieldName])) return choices[fieldName];
  return [];
};

const isDateField = (name) =>
  /(^|_)(date|expiry_date|acquisition_date|certificate_issue_date)$/.test(
    name,
  ) || name.endsWith('_date');

const getFieldType = (name, choices) => {
  if (choices.length) return 'select';
  if (name === 'card_expiry' && !name.includes('date')) return 'month';
  if (isDateField(name)) return 'date';
  if (name.endsWith('_year')) return 'number';
  return 'text';
};

const getMaxLength = (name) => {
  if (name === 'individual_ref_number') return 4;
  if (name === 'document_number' || name === 'card_number') return 64;
  if (name === 'card_expiry' || name === 'date_of_event') return 10;
  return undefined;
};

export const mapAdditionalDocumentTypeChoices = (choices = []) =>
  choices
    .filter((item) => item?.code)
    .map((item) => ({
      value: item.code,
      code: item.code,
      label: item.label || titleCase(item.code),
      enabled: item.enabled !== false,
      requiredFields: item.required_fields || [],
      anyOfFields: item.any_of_fields || [],
      requiresBackFile: Boolean(item.requires_back_file),
      fieldChoices: item.field_choices || {},
      enumItem: item,
    }));

export const getAdditionalDocumentTypeKey = (documentType) =>
  normalise(
    documentType?.code ??
      documentType?.value?.code ??
      documentType?.value ??
      documentType?.id ??
      documentType?.label ??
      documentType,
  );

export const getAdditionalDocumentFields = (documentType) => {
  const enumItem = documentType?.enumItem || documentType || {};
  const requiredFields =
    enumItem.required_fields || documentType?.requiredFields || [];
  const anyOfFields = enumItem.any_of_fields || documentType?.anyOfFields || [];
  const names = [...new Set([...requiredFields, ...anyOfFields])].filter(
    (name) => name !== 'file' && name !== 'back_file' && name !== 'backFile',
  );

  return names.map((name) => {
    const choices = choicesFor(enumItem, name);
    return {
      name,
      label: getFieldLabel(name),
      type: getFieldType(name, choices),
      required: requiredFields.includes(name),
      requiredGroup: anyOfFields.includes(name) ? 'any_of_fields' : undefined,
      maxLength: getMaxLength(name),
      options: choices.map((choice) => ({
        value: choiceValue(choice),
        label: choiceLabel(choice),
      })),
    };
  });
};

export const requiresBackFile = (documentType) => {
  if (
    documentType?.requiresBackFile ??
    documentType?.enumItem?.requires_back_file ??
    false
  ) {
    return true;
  }
  /* FIX (driving licence back file): a driving licence always has a
     front and a back side, even when the enums/preview response does
     not set requires_back_file for it (e.g. the document was hydrated
     from photographic_documents in the preview API response). */
  return getAdditionalDocumentTypeKey(documentType).includes('driving_licence');
};

export const findAdditionalDocumentOption = (
  documentType,
  options = ADDITIONAL_DOCUMENT_TYPES,
) => {
  const code = documentType?.document_type_code || documentType?.code;
  const key = getAdditionalDocumentTypeKey(documentType);
  return (
    options.find(
      (option) =>
        option.code === code || getAdditionalDocumentTypeKey(option) === key,
    ) || null
  );
};

export const emptyAdditionalDocumentValues = (documentType) =>
  getAdditionalDocumentFields(documentType).reduce((values, field) => {
    values[field.name] = '';
    return values;
  }, {});
